import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ChatWebSocketClient, ConnectionState } from '../lib/websocket/chat-client';
import { logger } from '../lib/utils/logger';

interface WebSocketContextType {
  client: ChatWebSocketClient | null;
  isConnected: boolean;
  connectionState: ConnectionState;
  connect: () => Promise<void>;
  disconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

/**
 * Provider component for WebSocket connection
 * Manages the lifecycle of the WebSocket client and provides it to children
 */
export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const clientRef = useRef<ChatWebSocketClient | null>(null);

  // Initialize client when user signs in
  useEffect(() => {
    if (!isSignedIn) {
      // Disconnect if user signs out
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
        setConnectionState('disconnected');
      }
      return;
    }

    // Create client instance
    const wsClient = new ChatWebSocketClient({
      getToken: async () => {
        try {
          return await getToken();
        } catch (error) {
          logger.error('Failed to get token for WebSocket', error);
          return null;
        }
      },
      onConnect: () => {
        logger.info('WebSocket connected');
      },
      onDisconnect: (reason) => {
        logger.info('WebSocket disconnected', { reason });
      },
      onError: (error) => {
        logger.error('WebSocket error', error);
      },
      onConnectionStateChange: (state) => {
        setConnectionState(state);
      },
    });

    clientRef.current = wsClient;
    setConnectionState('disconnected');

    // Auto-connect when client is created
    wsClient.connect().catch((error) => {
      logger.error('Failed to connect WebSocket on mount', error);
    });

    // Cleanup on unmount
    return () => {
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
        setConnectionState('disconnected');
      }
    };
  }, [isSignedIn, getToken]);

  const connect = useCallback(async () => {
    if (clientRef.current) {
      await clientRef.current.connect();
    }
  }, []);

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect();
    }
  }, []);

  const isConnected = connectionState === 'connected';

  return (
    <WebSocketContext.Provider
      value={{
        client: clientRef.current,
        isConnected,
        connectionState,
        connect,
        disconnect,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket context
 */
export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
